import "./FormLogin.css";
import { useState } from "react";
import { Label } from "flowbite-react";
import { NavLink } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { updateIsConnect, updateNotifNumber, updateUserRole, updatePseudo, updateBadge, resetUser, updateRegion } from "../../store/userSlice";
import { object, string } from "yup";
import { useFormik } from "formik";
import { loginUser } from "../../services/callAPI/toLoginUser";
import ValidToast from "../toasts/ValidToast";
import UnvalidToast from "../toasts/UnvalidToast";
import { noInvisibleChars } from "../../services/hooks/sanitizeValue/noInvisibleChar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { clearTokens, storeRefreshToken, storeToken } from "../../services/hooks/storage/tokenStorage";

//setting componentFormLogin
export function FormLogin() {
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isShowValidToast, setIsShowValidToast] = useState<boolean>(false);
  const [isShowUnValidToast, setIsShowUnValidToast] = useState<boolean>(false);

  //set validation schema with Yup
  const registerSchema = object({
    email: string()
      .trim()
      .required(`Veuillez renseigner votre adresse mail`)
      .email(`Doit être un email valide`)
      .min(5, "L'email doit comporter au moins 5 caractères")
      .max(254, "L'email ne peut pas dépasser 254 caractères")
      .test("is-email-valid", "l'email contient des caractères non-imprimables", (value) => noInvisibleChars(value))
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,  // Expression régulière pour filtrer les domaines invalides
        "Le domaine de l'email doit être valide"
      )
      .test(//check if domain name is valid to way strict
        "is-valid-domain",
        "Le domaine de l'email est invalide",
        (value) => {
          if (!value) return false;
          const domainPattern = /^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
          return domainPattern.test(value);
        }
      ),
    password: string()
      .trim()
      .required("Mot de passe requis")
      .min(8, "8 caractères minimum")
      .max(64, "64 caractères maximum")
      .test("is-password-valid", "password contient des caractères non-imprimables", (value) => noInvisibleChars(value))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$#%!*+?&-.;:,])[a-zA-Z0-9@$!%*+?&.#-;:,]{8,64}$/, "Doit contenir au moins une majuscule, une minus, un chiffre et un caractère spécial")
  })

  //set formik
  const { handleSubmit, handleChange, errors, touched, isSubmitting } = useFormik({
    initialValues: {
      email: "",
      password: ""
    },
    validationSchema: registerSchema,
    onSubmit: async values => {
      try {
        //call API
        const data = await loginUser(values);

        //if success login => stock token into localStorage
        await storeToken(data.token);

        //if success login => stock refreshToken into localStorage
        await storeRefreshToken(data.refreshToken);

        //update redux's sliceUser
        dispatch(updateUserRole(data.role));

        dispatch(updatePseudo(data.pseudo));

        dispatch(updateNotifNumber(data.notifNumber));

        dispatch(updateBadge(data.badges));

        dispatch(updateIsConnect(true));

        dispatch(updateRegion(data.region));

        //show success toast
        setIsShowValidToast(true);

        if (data.role === "TRAVELER") {
          //redirect to connexion page
          location.href = "/trade_requests";
        }
        else if (data.role === "RESIDENT") {
          //redirect to connexion page
          location.href = "/trade_propositions";
        }
        else {
          // put all variables to scratch and redirect to home page
          alert('role inconnu');

          await clearTokens();

          dispatch(resetUser());
          location.href = "/";
        }
      } catch (error) {
        setIsShowUnValidToast(true);
      }
    }
  })

  return (
    <article className="flex flex-col items-center mx-6">
      {isShowValidToast && <ValidToast onClose={() => setIsShowValidToast(false)} />}
      {isShowUnValidToast && <UnvalidToast onClose={() => setIsShowUnValidToast(false)} />}

      <h2 className="max-w-md title2">Je suis déja membre</h2>

      {/* Formulaire */}
      <form className="flex max-w-md flex-col gap-y-11" onSubmit={handleSubmit}>

        {/* Je rentre l'email */}
        <div>
          <div className="mb-2 block text-left">
            <Label htmlFor="email" value="Email" />
          </div>
          <input autoComplete="email" className="border border-gray-500 rounded-md bg-gray-300 w-full" id="email" type="email" placeholder="eleonore@hotmail.com" onChange={handleChange} required />
          {touched.email && errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
        </div>

        {/* Je rentre le mot de passe */}
        <div>
          <div className="mb-2 block text-left">
            <Label htmlFor="password1" value="Mot de passe" />
          </div>
          <div className="relative">
            <input
              autoComplete="current-password"
              className="border border-gray-500 rounded-md bg-gray-300 w-full"
              id="password1"
              name="password"
              type={showPassword ? "text" : "password"}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-sm"
            >
              {showPassword ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
            </button>
          </div>
          {touched.password && errors.password && <span className="text-red-500 text-sm">{errors.password}</span>}
        </div>

        {/* Je soumets le formulaire*/}
        <button style={{ backgroundColor: '#335C67', color: 'white' }} className="rounded-2xl py-2" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Envoi en cours...' : `Se connecter`}
        </button>

        <NavLink className="underline" to={"/password-forgot"}>Mot de passe oublié ?</NavLink>
      </form>
    </article>
  );
}
